import React, { useState, Children } from 'react';
import { Search, Grid, List, Filter, ChevronDown, Briefcase, User, Users, Crown, Star, Eye, Pin, FileText, Upload, Plus, Download, Share2, Edit, MoreVertical, Copy, Lock, Building2, Home, DollarSign, Heart, Scale, Wrench, Globe, Sparkles } from 'lucide-react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { DocumentEditor } from './DocumentEditor';
import { PrefillSelectionModal } from '../components/modals/PrefillSelectionModal';
import { TemplateDetailsSidebar } from '../components/modals/TemplateDetailsSidebar';
interface Template {
  id: string;
  title: string;
  category: string;
  mainCategory: string;
  subcategory: string;
  language: 'EN' | 'FR';
  flag: string;
  isPremium: boolean;
  extraCount?: number;
  isPinned?: boolean;
  description?: string;
}
interface Document {
  id: string;
  documentNumber: string;
  name: string;
  createdAt: string;
  status: string;
}
const MOCK_TEMPLATES: Template[] = [
// Work & Employment - Hiring
{
  id: '1',
  title: 'Job offer letter',
  category: 'EMPLOYMENT',
  mainCategory: 'work',
  subcategory: 'hiring',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Officially offer a position to a new hire'
}, {
  id: '2',
  title: 'Basic employment contract',
  category: 'EMPLOYMENT',
  mainCategory: 'work',
  subcategory: 'hiring',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Simple written agreement between employer and employee'
}, {
  id: '3',
  title: 'Freelancer job offer',
  category: 'EMPLOYMENT',
  mainCategory: 'work',
  subcategory: 'hiring',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Offer a contract role to a non-permanent worker'
}, {
  id: '4',
  title: 'Contractor agreement',
  category: 'EMPLOYMENT',
  mainCategory: 'work',
  subcategory: 'hiring',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Hire someone who works for themselves'
},
// Work & Employment - Leaving
{
  id: '5',
  title: 'Leaving the company agreement',
  category: 'EMPLOYMENT',
  mainCategory: 'work',
  subcategory: 'leaving',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Wrap things up when an employee departs'
}, {
  id: '6',
  title: 'Termination letter',
  category: 'EMPLOYMENT',
  mainCategory: 'work',
  subcategory: 'leaving',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'The official letter when letting an employee go'
}, {
  id: '7',
  title: 'Final sign-off on employment',
  category: 'EMPLOYMENT',
  mainCategory: 'work',
  subcategory: 'leaving',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'No further obligations after employment ends'
},
// Work & Employment - Performance
{
  id: '8',
  title: 'Employee improvement plan',
  category: 'EMPLOYMENT',
  mainCategory: 'work',
  subcategory: 'performance',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Help an underperforming employee get back on track'
},
// Work & Employment - Confidentiality
{
  id: '9',
  title: 'Employee secrecy agreement',
  category: 'EMPLOYMENT',
  mainCategory: 'work',
  subcategory: 'confidentiality',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: true,
  description: 'Employee agrees not to share confidential information'
},
// Workplace Policies - Health & Safety
{
  id: '10',
  title: 'Workplace health & safety policy',
  category: 'WORKPLACE',
  mainCategory: 'workplace',
  subcategory: 'health-safety',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Guidelines to maintain a safe working environment'
}, {
  id: '11',
  title: 'Emergency procedures manual',
  category: 'WORKPLACE',
  mainCategory: 'workplace',
  subcategory: 'health-safety',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Step-by-step instructions for workplace emergencies'
}, {
  id: '12',
  title: 'Incident reporting policy',
  category: 'WORKPLACE',
  mainCategory: 'workplace',
  subcategory: 'health-safety',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'How to report accidents or near-misses at work'
}, {
  id: '13',
  title: 'First aid policy',
  category: 'WORKPLACE',
  mainCategory: 'workplace',
  subcategory: 'health-safety',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Rules regarding first aid administration and supplies'
}, {
  id: '14',
  title: 'COVID-19 workplace policy',
  category: 'WORKPLACE',
  mainCategory: 'workplace',
  subcategory: 'health-safety',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Health protocols for infectious diseases'
},
// Workplace Policies - Conduct
{
  id: '15',
  title: 'No violence at work',
  category: 'WORKPLACE',
  mainCategory: 'workplace',
  subcategory: 'conduct',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: true,
  description: 'Zero-tolerance rules for violence in the workplace'
}, {
  id: '16',
  title: 'No harassment or bullying',
  category: 'WORKPLACE',
  mainCategory: 'workplace',
  subcategory: 'conduct',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Protection from harassment and discrimination'
}, {
  id: '17',
  title: 'Anti-harassment (short version)',
  category: 'WORKPLACE',
  mainCategory: 'workplace',
  subcategory: 'conduct',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: true,
  description: 'Simplified harassment policy for quick reference'
},
// Workplace Policies - Technology
{
  id: '18',
  title: 'Cybersecurity policy',
  category: 'WORKPLACE',
  mainCategory: 'workplace',
  subcategory: 'technology',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Rules for protecting company data and systems'
}, {
  id: '19',
  title: 'Acceptable use of technology',
  category: 'WORKPLACE',
  mainCategory: 'workplace',
  subcategory: 'technology',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Guidelines for using company computers and internet'
},
// Workplace Policies - Accommodation
{
  id: '20',
  title: 'Workplace accommodation policy',
  category: 'WORKPLACE',
  mainCategory: 'workplace',
  subcategory: 'accommodation',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Process for requesting disability or religious accommodations'
},
// Business Agreements - Services
{
  id: '21',
  title: 'Service agreement',
  category: 'BUSINESS',
  mainCategory: 'business',
  subcategory: 'services',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Standard contract for providing professional services'
}, {
  id: '22',
  title: 'Consulting agreement',
  category: 'BUSINESS',
  mainCategory: 'business',
  subcategory: 'services',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Contract for independent consulting services'
}, {
  id: '23',
  title: 'Contrat de services avec client',
  category: 'BUSINESS',
  mainCategory: 'business',
  subcategory: 'services',
  language: 'FR',
  flag: 'https://digilabs.nyc3.digitaloceanspaces.com/lawvo/53e79ee3fdf169e2a1f904bdb8be8c77.png',
  isPremium: true,
  isPinned: false,
  description: 'French language service agreement for Quebec'
}, {
  id: '24',
  title: 'Maintenance services agreement',
  category: 'BUSINESS',
  mainCategory: 'business',
  subcategory: 'services',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Contract for ongoing maintenance work'
}, {
  id: '25',
  title: 'Professional services contract',
  category: 'BUSINESS',
  mainCategory: 'business',
  subcategory: 'services',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Detailed agreement for specialized professional work'
}, {
  id: '26',
  title: 'IT services agreement',
  category: 'BUSINESS',
  mainCategory: 'business',
  subcategory: 'services',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Contract for technology support and services'
},
// Business Agreements - Partnerships
{
  id: '27',
  title: 'Partnership agreement',
  category: 'BUSINESS',
  mainCategory: 'business',
  subcategory: 'partnerships',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Foundational document for business partners'
}, {
  id: '28',
  title: 'Joint venture agreement',
  category: 'BUSINESS',
  mainCategory: 'business',
  subcategory: 'partnerships',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Contract for a specific collaborative project'
}, {
  id: '29',
  title: 'Shareholder agreement',
  category: 'BUSINESS',
  mainCategory: 'business',
  subcategory: 'partnerships',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Rules governing corporate shareholders'
},
// Business Agreements - Sales
{
  id: '30',
  title: 'Bill of sale',
  category: 'BUSINESS',
  mainCategory: 'business',
  subcategory: 'sales',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Receipt for the transfer of personal property'
}, {
  id: '31',
  title: 'Purchase agreement',
  category: 'BUSINESS',
  mainCategory: 'business',
  subcategory: 'sales',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Contract for buying or selling goods'
}, {
  id: '32',
  title: 'Sales contract',
  category: 'BUSINESS',
  mainCategory: 'business',
  subcategory: 'sales',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Standard terms for commercial sales'
}, {
  id: '33',
  title: 'Invoice template',
  category: 'BUSINESS',
  mainCategory: 'business',
  subcategory: 'sales',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Standardized billing document for clients'
},
// Business Agreements - Licensing
{
  id: '34',
  title: 'Software license agreement',
  category: 'BUSINESS',
  mainCategory: 'business',
  subcategory: 'licensing',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Terms for using proprietary software'
}, {
  id: '35',
  title: 'Trademark license agreement',
  category: 'BUSINESS',
  mainCategory: 'business',
  subcategory: 'licensing',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Permission to use brand assets and logos'
},
// Renting & Property - Residential
{
  id: '36',
  title: 'Residential lease agreement',
  category: 'RENTING',
  mainCategory: 'renting',
  subcategory: 'residential',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Standard contract for renting a home or apartment'
}, {
  id: '37',
  title: 'Month-to-month rental agreement',
  category: 'RENTING',
  mainCategory: 'renting',
  subcategory: 'residential',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Flexible short-term housing contract'
}, {
  id: '38',
  title: 'Lease renewal agreement',
  category: 'RENTING',
  mainCategory: 'renting',
  subcategory: 'residential',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Extend an existing tenancy agreement'
}, {
  id: '39',
  title: 'Roommate agreement',
  category: 'RENTING',
  mainCategory: 'renting',
  subcategory: 'residential',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Rules and cost-sharing for shared housing'
}, {
  id: '40',
  title: 'Lease termination notice',
  category: 'RENTING',
  mainCategory: 'renting',
  subcategory: 'residential',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Official notice to end a tenancy'
},
// Renting & Property - Commercial
{
  id: '41',
  title: 'Commercial lease agreement',
  category: 'RENTING',
  mainCategory: 'renting',
  subcategory: 'commercial',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Contract for renting business premises'
}, {
  id: '42',
  title: 'Office space rental agreement',
  category: 'RENTING',
  mainCategory: 'renting',
  subcategory: 'commercial',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Specific terms for renting office suites'
}, {
  id: '43',
  title: 'Retail lease agreement',
  category: 'RENTING',
  mainCategory: 'renting',
  subcategory: 'commercial',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Lease terms tailored for storefront businesses'
},
// Renting & Property - Subletting
{
  id: '44',
  title: 'Sublease agreement',
  category: 'RENTING',
  mainCategory: 'renting',
  subcategory: 'subletting',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Contract to rent from an existing tenant'
}, {
  id: '45',
  title: 'Subletting consent form',
  category: 'RENTING',
  mainCategory: 'renting',
  subcategory: 'subletting',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Landlord approval for a sublet arrangement'
},
// Strata & Condo - Bylaws
{
  id: '46',
  title: 'Strata bylaws template',
  category: 'STRATA',
  mainCategory: 'strata',
  subcategory: 'bylaws',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Standard rules for strata corporations'
}, {
  id: '47',
  title: 'Condo rules & regulations',
  category: 'STRATA',
  mainCategory: 'strata',
  subcategory: 'bylaws',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Day-to-day rules for condominium residents'
}, {
  id: '48',
  title: 'Pet bylaw amendment',
  category: 'STRATA',
  mainCategory: 'strata',
  subcategory: 'bylaws',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Specific rules regarding animals in the building'
}, {
  id: '49',
  title: 'Parking bylaw',
  category: 'STRATA',
  mainCategory: 'strata',
  subcategory: 'bylaws',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Regulations for resident and visitor parking'
},
// Strata & Condo - Governance
{
  id: '50',
  title: 'AGM meeting minutes template',
  category: 'STRATA',
  mainCategory: 'strata',
  subcategory: 'governance',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Format for recording annual general meetings'
}, {
  id: '51',
  title: 'Strata council election form',
  category: 'STRATA',
  mainCategory: 'strata',
  subcategory: 'governance',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Nomination and voting form for council members'
}, {
  id: '52',
  title: 'Proxy voting form',
  category: 'STRATA',
  mainCategory: 'strata',
  subcategory: 'governance',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Authorize someone else to vote on your behalf'
},
// Strata & Condo - Maintenance
{
  id: '53',
  title: 'Maintenance request form',
  category: 'STRATA',
  mainCategory: 'strata',
  subcategory: 'maintenance',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Official form to request building repairs'
}, {
  id: '54',
  title: 'Renovation approval form',
  category: 'STRATA',
  mainCategory: 'strata',
  subcategory: 'maintenance',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Request permission for in-suite modifications'
},
// Money & Loans - Loans
{
  id: '55',
  title: 'Promissory note',
  category: 'MONEY',
  mainCategory: 'money',
  subcategory: 'loans',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'A written promise to repay a specific amount'
}, {
  id: '56',
  title: 'Personal loan agreement',
  category: 'MONEY',
  mainCategory: 'money',
  subcategory: 'loans',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Contract for lending money between individuals'
}, {
  id: '57',
  title: 'Business loan agreement',
  category: 'MONEY',
  mainCategory: 'money',
  subcategory: 'loans',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Formal contract for commercial lending'
}, {
  id: '58',
  title: 'Line of credit agreement',
  category: 'MONEY',
  mainCategory: 'money',
  subcategory: 'loans',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Terms for revolving credit access'
},
// Money & Loans - Debt
{
  id: '59',
  title: 'Debt settlement agreement',
  category: 'MONEY',
  mainCategory: 'money',
  subcategory: 'debt',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Agreement to resolve outstanding debts'
}, {
  id: '60',
  title: 'Debt acknowledgment letter',
  category: 'MONEY',
  mainCategory: 'money',
  subcategory: 'debt',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Formal recognition of money owed'
}, {
  id: '61',
  title: 'Demand letter for payment',
  category: 'MONEY',
  mainCategory: 'money',
  subcategory: 'debt',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Final request for payment before legal action'
},
// Money & Loans - Investment
{
  id: '62',
  title: 'Investment agreement',
  category: 'MONEY',
  mainCategory: 'money',
  subcategory: 'investment',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Terms for investing capital in a business'
}, {
  id: '63',
  title: 'Convertible note agreement',
  category: 'MONEY',
  mainCategory: 'money',
  subcategory: 'investment',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Short-term debt that converts to equity'
},
// Money & Loans - Guarantor
{
  id: '64',
  title: 'Guarantor agreement',
  category: 'MONEY',
  mainCategory: 'money',
  subcategory: 'guarantor',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Promise to pay if the primary borrower defaults'
},
// Family & Personal - Marriage
{
  id: '65',
  title: 'Prenuptial agreement',
  category: 'FAMILY',
  mainCategory: 'family',
  subcategory: 'marriage',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Asset protection contract before marriage'
}, {
  id: '66',
  title: 'Cohabitation agreement',
  category: 'FAMILY',
  mainCategory: 'family',
  subcategory: 'marriage',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Rules for unmarried couples living together'
}, {
  id: '67',
  title: 'Marriage contract',
  category: 'FAMILY',
  mainCategory: 'family',
  subcategory: 'marriage',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Financial agreement between spouses'
},
// Family & Personal - Children
{
  id: '68',
  title: 'Child custody agreement',
  category: 'FAMILY',
  mainCategory: 'family',
  subcategory: 'children',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Arrangements for care and decision-making'
}, {
  id: '69',
  title: 'Parenting plan',
  category: 'FAMILY',
  mainCategory: 'family',
  subcategory: 'children',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Detailed schedule for co-parenting'
},
// Family & Personal - Separation
{
  id: '70',
  title: 'Separation agreement',
  category: 'FAMILY',
  mainCategory: 'family',
  subcategory: 'separation',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Terms for living apart before divorce'
}, {
  id: '71',
  title: 'Divorce settlement agreement',
  category: 'FAMILY',
  mainCategory: 'family',
  subcategory: 'separation',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Final resolution of marital issues'
}, {
  id: '72',
  title: 'Property division agreement',
  category: 'FAMILY',
  mainCategory: 'family',
  subcategory: 'separation',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'How shared assets will be split'
}, {
  id: '73',
  title: 'Spousal support agreement',
  category: 'FAMILY',
  mainCategory: 'family',
  subcategory: 'separation',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Terms for ongoing financial assistance'
},
// Family & Personal - Personal Assets
{
  id: '74',
  title: 'Personal asset inventory',
  category: 'FAMILY',
  mainCategory: 'family',
  subcategory: 'personal-assets',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Comprehensive list of belongings and value'
}, {
  id: '75',
  title: 'Joint bank account agreement',
  category: 'FAMILY',
  mainCategory: 'family',
  subcategory: 'personal-assets',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Rules for shared financial accounts'
},
// Wills & Estate - Wills
{
  id: '76',
  title: 'Last will and testament',
  category: 'WILLS',
  mainCategory: 'wills',
  subcategory: 'wills',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Comprehensive instructions for your estate'
}, {
  id: '77',
  title: 'Simple will',
  category: 'WILLS',
  mainCategory: 'wills',
  subcategory: 'wills',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Basic estate distribution instructions'
}, {
  id: '78',
  title: 'Will planning guide',
  category: 'WILLS',
  mainCategory: 'wills',
  subcategory: 'wills',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Checklist to prepare for writing a will'
}, {
  id: '79',
  title: 'Codicil to will',
  category: 'WILLS',
  mainCategory: 'wills',
  subcategory: 'wills',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Document to amend an existing will'
}, {
  id: '80',
  title: 'Mirror wills for couples',
  category: 'WILLS',
  mainCategory: 'wills',
  subcategory: 'wills',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Identical wills for spouses or partners'
},
// Wills & Estate - Power of Attorney
{
  id: '81',
  title: 'Continuing power of attorney for property',
  category: 'WILLS',
  mainCategory: 'wills',
  subcategory: 'power-of-attorney',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Authorize someone to manage your finances'
}, {
  id: '82',
  title: 'Power of attorney for personal care',
  category: 'WILLS',
  mainCategory: 'wills',
  subcategory: 'power-of-attorney',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Authorize someone to make health decisions'
}, {
  id: '83',
  title: 'General power of attorney',
  category: 'WILLS',
  mainCategory: 'wills',
  subcategory: 'power-of-attorney',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Temporary authorization for specific acts'
},
// Wills & Estate - Trusts
{
  id: '84',
  title: 'Living trust agreement',
  category: 'WILLS',
  mainCategory: 'wills',
  subcategory: 'trusts',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Manage assets during life and after death'
}, {
  id: '85',
  title: 'Family trust deed',
  category: 'WILLS',
  mainCategory: 'wills',
  subcategory: 'trusts',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Hold assets for the benefit of family members'
},
// Wills & Estate - Estate Planning
{
  id: '86',
  title: 'Estate planning checklist',
  category: 'WILLS',
  mainCategory: 'wills',
  subcategory: 'estate-planning',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Guide to organizing your affairs'
}, {
  id: '87',
  title: 'Beneficiary designation form',
  category: 'WILLS',
  mainCategory: 'wills',
  subcategory: 'estate-planning',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Name who receives specific assets'
},
// Home & Services - Maintenance
{
  id: '88',
  title: 'Home maintenance services agreement',
  category: 'HOME',
  mainCategory: 'home-services',
  subcategory: 'maintenance',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Contract for ongoing home maintenance work'
}, {
  id: '89',
  title: 'Lawn care & landscaping contract',
  category: 'HOME',
  mainCategory: 'home-services',
  subcategory: 'maintenance',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Agreement for yard and garden services'
}, {
  id: '90',
  title: 'Snow removal services agreement',
  category: 'HOME',
  mainCategory: 'home-services',
  subcategory: 'maintenance',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Seasonal contract for snow clearing services'
},
// Home & Services - Renovations
{
  id: '91',
  title: 'Home renovation contract',
  category: 'HOME',
  mainCategory: 'home-services',
  subcategory: 'renovations',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: true,
  isPinned: false,
  description: 'Agreement for residential renovation projects'
}, {
  id: '92',
  title: 'Contractor work order',
  category: 'HOME',
  mainCategory: 'home-services',
  subcategory: 'renovations',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Detailed scope of work for a specific project'
},
// Home & Services - Cleaning
{
  id: '93',
  title: 'Residential cleaning services agreement',
  category: 'HOME',
  mainCategory: 'home-services',
  subcategory: 'cleaning',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'Contract for regular house cleaning services'
}, {
  id: '94',
  title: 'Move-in/move-out cleaning contract',
  category: 'HOME',
  mainCategory: 'home-services',
  subcategory: 'cleaning',
  language: 'EN',
  flag: '🇨🇦',
  isPremium: false,
  isPinned: false,
  description: 'One-time deep cleaning for property transitions'
},
// En Français - Emploi
{
  id: '95',
  title: 'Contrat de travail',
  category: 'FRENCH',
  mainCategory: 'en-francais',
  subcategory: 'emploi',
  language: 'FR',
  flag: 'https://digilabs.nyc3.digitaloceanspaces.com/lawvo/53e79ee3fdf169e2a1f904bdb8be8c77.png',
  isPremium: false,
  isPinned: false,
  description: "Contrat d'emploi standard pour le Québec"
}, {
  id: '96',
  title: "Lettre d'offre d'emploi",
  category: 'FRENCH',
  mainCategory: 'en-francais',
  subcategory: 'emploi',
  language: 'FR',
  flag: 'https://digilabs.nyc3.digitaloceanspaces.com/lawvo/53e79ee3fdf169e2a1f904bdb8be8c77.png',
  isPremium: false,
  isPinned: false,
  description: 'Offre formelle de poste à un candidat'
},
// En Français - Affaires
{
  id: '97',
  title: 'Accord de partenariat',
  category: 'FRENCH',
  mainCategory: 'en-francais',
  subcategory: 'affaires',
  language: 'FR',
  flag: 'https://digilabs.nyc3.digitaloceanspaces.com/lawvo/53e79ee3fdf169e2a1f904bdb8be8c77.png',
  isPremium: true,
  isPinned: false,
  description: "Entente entre partenaires d'affaires"
}, {
  id: '98',
  title: 'Entente de confidentialité (NDA)',
  category: 'FRENCH',
  mainCategory: 'en-francais',
  subcategory: 'affaires',
  language: 'FR',
  flag: 'https://digilabs.nyc3.digitaloceanspaces.com/lawvo/53e79ee3fdf169e2a1f904bdb8be8c77.png',
  isPremium: false,
  isPinned: false,
  description: 'Accord de non-divulgation en français'
},
// En Français - Famille
{
  id: '99',
  title: 'Convention de séparation',
  category: 'FRENCH',
  mainCategory: 'en-francais',
  subcategory: 'famille',
  language: 'FR',
  flag: 'https://digilabs.nyc3.digitaloceanspaces.com/lawvo/53e79ee3fdf169e2a1f904bdb8be8c77.png',
  isPremium: true,
  isPinned: false,
  description: 'Entente pour les couples qui se séparent'
}, {
  id: '100',
  title: "Accord de garde d'enfants",
  category: 'FRENCH',
  mainCategory: 'en-francais',
  subcategory: 'famille',
  language: 'FR',
  flag: 'https://digilabs.nyc3.digitaloceanspaces.com/lawvo/53e79ee3fdf169e2a1f904bdb8be8c77.png',
  isPremium: true,
  isPinned: false,
  description: 'Arrangements pour la garde des enfants'
},
// En Français - Immobilier
{
  id: '101',
  title: 'Bail résidentiel',
  category: 'FRENCH',
  mainCategory: 'en-francais',
  subcategory: 'immobilier',
  language: 'FR',
  flag: 'https://digilabs.nyc3.digitaloceanspaces.com/lawvo/53e79ee3fdf169e2a1f904bdb8be8c77.png',
  isPremium: false,
  isPinned: false,
  description: 'Contrat de location pour logement au Québec'
}];
const MOCK_DOCUMENTS: Document[] = [{
  id: '1',
  documentNumber: '3037',
  name: 'AODA ACCESSIBILITY STANDARDS POLICY (6)',
  createdAt: 'January 19, 2026',
  status: 'Request a Review'
}, {
  id: '2',
  documentNumber: '2957',
  name: 'PROMISSORY NOTE (4)',
  createdAt: 'October 31, 2025',
  status: 'Request a Review'
}, {
  id: '3',
  documentNumber: '2956',
  name: 'PROMISSORY NOTE (3)',
  createdAt: 'October 31, 2025',
  status: 'Request a Review'
}, {
  id: '4',
  documentNumber: '2955',
  name: 'PROMISSORY NOTE (2)',
  createdAt: 'October 31, 2025',
  status: 'Request a Review'
}, {
  id: '5',
  documentNumber: '2954',
  name: 'PROMISSORY NOTE (1)',
  createdAt: 'October 31, 2025',
  status: 'Request a Review'
}];
interface AllTemplatesProps {
  onMenuClick?: () => void;
  onNavigateToAICopilot?: () => void;
  onNavigate?: (path: string) => void;
}
const MOCK_USER_BUSINESSES = [{
  id: '1',
  name: 'Maple Leaf Restaurant',
  address: '123 Queen Street West',
  city: 'Toronto',
  province: 'Ontario',
  postalCode: 'M5H 2N2',
  hstNumber: '123456789RT0001',
  registrationNumber: 'ON-12345',
  isDefault: true
}, {
  id: '2',
  name: 'Northern Lights Consulting',
  address: '456 Yonge Street',
  city: 'Toronto',
  province: 'Ontario',
  postalCode: 'M4Y 1X5',
  hstNumber: '987654321RT0002',
  registrationNumber: 'ON-67890',
  isDefault: false
}, {
  id: '3',
  name: 'Pacific Coast Trading Co.',
  address: '789 Granville Street',
  city: 'Vancouver',
  province: 'British Columbia',
  postalCode: 'V6Z 1K3',
  hstNumber: '456789123RT0003',
  registrationNumber: 'BC-24680',
  isDefault: false
}];
const MOCK_USER_FAMILY = [{
  id: '1',
  firstName: 'Sarah',
  lastName: 'Johnson',
  relationship: 'Spouse',
  isDefault: true
}];
const MOCK_USER_EMPLOYEES = [{
  id: '1',
  firstName: 'Michael',
  lastName: 'Smith',
  position: 'Manager',
  email: 'michael.smith@example.com',
  phone: '+1 (416) 555-0123',
  isDefault: true
}];
interface DashboardContext {
  toggleMobileSidebar: () => void;
}
// 1 featured card per category (not necessarily first)
const FEATURED_IDS = new Set(['3', '13', '25', '39', '49', '57', '70', '79', '91', '98' // En Français - Entente de confidentialité
]);
// 2 non-premium purchasable cards per category
const PURCHASABLE_IDS = new Set(['2', '3', '11', '14', '30', '32', '37', '40', '48', '51', '55', '60', '69', '74', '77', '83', '89', '93', '95', '98' // En Français
]);
function TemplateCard({
  template,
  onTemplateClick,
  onUpgradeClick,
  isFeatured = false
}: {
  template: Template;
  onTemplateClick: (t: Template) => void;
  onUpgradeClick: () => void;
  isFeatured?: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);
  // Helper to convert hex to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  // Category color mapping for featured cards
  const categoryAccentColors: Record<string, string> = {
    EMPLOYMENT: '#1B5E20',
    WORKPLACE: '#0D47A1',
    BUSINESS: '#4A148C',
    RENTING: '#E65100',
    STRATA: '#880E4F',
    MONEY: '#F57F17',
    FAMILY: '#00695C',
    WILLS: '#37474F',
    HOME: '#2E7D32',
    FRENCH: '#1565C0'
  };
  // Category light tint colors for featured card backgrounds
  const categoryLightTints: Record<string, string> = {
    EMPLOYMENT: '#E8F5E9',
    WORKPLACE: '#E3F2FD',
    BUSINESS: '#F3E5F5',
    RENTING: '#FFF3E0',
    STRATA: '#FCE4EC',
    MONEY: '#FFF8E1',
    FAMILY: '#E0F2F1',
    WILLS: '#ECEFF1',
    HOME: '#E8F5E9',
    FRENCH: '#E3F2FD'
  };
  // Darkest shade for hover state on purchasable CTA
  const darkAccentColors: Record<string, string> = {
    EMPLOYMENT: '#0F4016',
    WORKPLACE: '#082F6E',
    BUSINESS: '#330E63',
    RENTING: '#993600',
    STRATA: '#5E0A37',
    MONEY: '#B85E0F',
    FAMILY: '#004940',
    WILLS: '#252F34',
    HOME: '#1B5720',
    FRENCH: '#0D4A8C'
  };
  const accentColor = categoryAccentColors[template.category] || '#00DACE';
  const lightTint = categoryLightTints[template.category] || '#F6F9FC';
  const darkAccent = darkAccentColors[template.category] || '#333333';
  const featuredBg = isFeatured ? hexToRgba(lightTint, 0.2) : 'white';
  // Premium featured cards revert to default border on hover (purple overlay covers everything)
  const featuredBorder = isFeatured && !(template.isPremium && isHovered) ? accentColor : '#E8EEF4';
  return <div onClick={() => onTemplateClick(template)} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} className="group bg-white rounded-xl overflow-hidden hover:shadow-md transition-all duration-200 flex flex-col cursor-pointer relative hover:-translate-y-1" style={{
    minHeight: '180px',
    backgroundColor: featuredBg,
    border: `1.5px solid ${featuredBorder}`,
    boxShadow: isFeatured ? `0 4px 12px ${hexToRgba(accentColor, 0.1)}` : undefined
  }} data-id="element-1207">
      {/* Colored Top Border - unique per category */}
      <div className="h-1" style={{
      backgroundColor: accentColor
    }} data-id="element-1208" />

      {/* Premium Upgrade Overlay */}
      {template.isPremium && <div className="absolute inset-0 bg-violetBlue/95 opacity-0 group-hover:opacity-100 transition-opacity z-10 flex flex-col items-center justify-center p-6" data-id="element-1209">
          <Lock className="w-8 h-8 text-white mb-3" data-id="element-1210" />
          <p className="text-white font-semibold mb-4" data-id="element-1211">Upgrade to access</p>
          <button onClick={e => {
        e.stopPropagation();
        onUpgradeClick();
      }} className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2" data-id="element-1212">
            View plans →
          </button>
        </div>}

      <div className="p-3 flex flex-col flex-1" data-id="element-1213">
        {/* Header: Flag & Language + Upgrade Badge + Pin */}
        <div className="flex items-start justify-between mb-2" data-id="element-1214">
          <div className="flex items-center gap-2" data-id="element-1215">
            {template.flag.startsWith('http') ? <img src={template.flag} alt="Flag" className="w-5 h-5 object-contain" data-id="element-1216" /> : <span className="text-lg" data-id="element-1217">{template.flag}</span>}
            <span className="text-xs text-secondary" data-id="element-1218">{template.language}</span>
            {template.isPremium && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-aliceBlue text-violetBlue rounded text-xs font-medium" data-id="element-1219">
                <Crown className="w-3 h-3" data-id="element-1220" />
                Premium
              </span>}
          </div>
          <button className="p-1 hover:bg-aliceBlue rounded-full text-secondary hover:text-violetBlue transition-colors" data-id="element-1221">
            <Pin className={`w-3.5 h-3.5 ${template.isPinned ? 'fill-current text-violetBlue' : ''}`} data-id="element-1222" />
          </button>
        </div>

        {/* Title */}
        <div className="mb-1" data-id="element-1223">
          <h3 className="text-sm font-semibold text-primary leading-tight line-clamp-2" data-id="element-1224">
            {template.title}
          </h3>
        </div>

        {/* Description */}
        {template.description && <p className="text-xs text-secondary leading-tight line-clamp-3 mb-2" data-id="element-1225">
            {template.description}
          </p>}

        <div className="flex-1" data-id="element-1226" />

        {/* Footer: Get for Price Button (for purchasable templates) */}
        {!template.isPremium && PURCHASABLE_IDS.has(template.id) && <button className="w-full py-2 flex items-center justify-center gap-1 transition-all duration-150 ease-in-out" style={{
        border: isHovered ? `2.5px solid ${darkAccent}` : '1px solid #E8EEF4',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 500,
        color: isHovered ? darkAccent : '#101D5B'
      }} onClick={e => e.stopPropagation()} data-id="element-1227">
            <Lock className="w-3.5 h-3.5" data-id="element-1228" />
            Get for $9.99
          </button>}
      </div>
    </div>;
}
export function AllTemplates({
  onMenuClick,
  onNavigateToAICopilot,
  onNavigate
}: AllTemplatesProps) {
  const navigate = useNavigate();
  const {
    toggleMobileSidebar
  } = useOutletContext<DashboardContext>() || {
    toggleMobileSidebar: () => {}
  };
  const [activeTab, setActiveTab] = useState<'templates' | 'documents'>('templates');
  const [activeCategory, setActiveCategory] = useState<'business' | 'personal' | 'hr'>('business');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState<'all' | 'EN' | 'FR'>('all');
  const [hasDocuments, setHasDocuments] = useState(false);
  const [debugJourneyMode, setDebugJourneyMode] = useState<1 | 2 | 3>(1);
  const [isEditingDocument, setIsEditingDocument] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [showPrefillModal, setShowPrefillModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [prefillSelections, setPrefillSelections] = useState<any>(null);
  const [showTemplateSidebar, setShowTemplateSidebar] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('all');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const documents = hasDocuments ? MOCK_DOCUMENTS : [];
  const handleUploadDocument = () => {
    console.log('Upload document clicked');
  };
  const handleCreateNew = () => {
    setSelectedTemplate({
      id: 'new',
      title: 'New Document',
      category: 'GENERAL',
      mainCategory: 'general',
      subcategory: 'general',
      language: 'EN',
      flag: '🇨🇦',
      isPremium: false
    });
    setShowPrefillModal(true);
  };
  const handleTemplateClick = (template: Template) => {
    setSelectedTemplate(template);
    setShowTemplateSidebar(true);
  };
  const handleViewSample = () => {
    console.log('View sample for template:', selectedTemplate?.title);
  };
  const handleCreateDocumentFromSidebar = () => {
    setShowTemplateSidebar(false);
    setIsEditingDocument(true);
  };
  const handleCloseSidebar = () => {
    setShowTemplateSidebar(false);
  };
  const handlePrefillContinue = (selections: any) => {
    setPrefillSelections(selections);
    setShowPrefillModal(false);
    setIsEditingDocument(true);
  };
  const handlePrefillCancel = () => {
    setShowPrefillModal(false);
    setSelectedTemplate(null);
  };
  const handleEditDocument = (doc: Document) => {
    setSelectedDocument(doc);
    setIsEditingDocument(true);
  };
  const handleBackFromEditor = () => {
    setIsEditingDocument(false);
    setSelectedDocument(null);
  };
  const handleCreateDocument = () => {
    setShowPrefillModal(true);
  };
  const handleNavigateToAICopilot = () => {
    if (onNavigateToAICopilot) {
      onNavigateToAICopilot();
    } else {
      navigate('/ai-copilot');
    }
  };
  const handleNavigate = (path: string) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };
  const handleMenuClick = () => {
    if (onMenuClick) {
      onMenuClick();
    } else {
      toggleMobileSidebar();
    }
  };
  if (isEditingDocument) {
    return <DocumentEditor onMenuClick={handleMenuClick} onNavigateToAICopilot={handleNavigateToAICopilot} onBack={handleBackFromEditor} prefillData={prefillSelections} templateTitle={selectedTemplate?.title} userBusinesses={MOCK_USER_BUSINESSES} userFamilyMembers={MOCK_USER_FAMILY} userEmployees={MOCK_USER_EMPLOYEES} data-id="element-1229" />;
  }
  const categoryColors: Record<string, {
    color: string;
    bg: string;
    border: string;
  }> = {
    all: {
      color: '#7854FF',
      bg: '#F0EBFF',
      border: '#7854FF'
    },
    work: {
      color: '#1B5E20',
      bg: '#E8F5E9',
      border: '#1B5E20'
    },
    workplace: {
      color: '#0D47A1',
      bg: '#E3F2FD',
      border: '#0D47A1'
    },
    business: {
      color: '#4A148C',
      bg: '#F3E5F5',
      border: '#4A148C'
    },
    renting: {
      color: '#E65100',
      bg: '#FFF3E0',
      border: '#E65100'
    },
    strata: {
      color: '#880E4F',
      bg: '#FCE4EC',
      border: '#880E4F'
    },
    money: {
      color: '#F57F17',
      bg: '#FFF8E1',
      border: '#F57F17'
    },
    family: {
      color: '#00695C',
      bg: '#E0F2F1',
      border: '#00695C'
    },
    wills: {
      color: '#37474F',
      bg: '#ECEFF1',
      border: '#37474F'
    },
    'home-services': {
      color: '#2E7D32',
      bg: '#E8F5E9',
      border: '#2E7D32'
    },
    'en-francais': {
      color: '#1565C0',
      bg: '#E3F2FD',
      border: '#1565C0'
    }
  };
  const categories = [{
    id: 'all',
    label: 'All documents',
    icon: Grid
  }, {
    id: 'work',
    label: 'Work & Employment',
    icon: Briefcase
  }, {
    id: 'workplace',
    label: 'Workplace Policies',
    icon: FileText
  }, {
    id: 'business',
    label: 'Business Agreements',
    icon: Users
  }, {
    id: 'renting',
    label: 'Renting & Property',
    icon: Home
  }, {
    id: 'strata',
    label: 'Strata & Condo',
    icon: Building2
  }, {
    id: 'money',
    label: 'Money & Loans',
    icon: DollarSign
  }, {
    id: 'family',
    label: 'Family & Personal',
    icon: Heart
  }, {
    id: 'wills',
    label: 'Wills & Estate',
    icon: Scale
  }, {
    id: 'home-services',
    label: 'Home & Services',
    icon: Wrench
  }, {
    id: 'en-francais',
    label: 'En Français',
    icon: Globe
  }];
  const subCategories: Record<string, {
    id: string;
    label: string;
    count: number | null;
  }[]> = {
    work: [{
      id: 'all',
      label: 'All',
      count: null
    }, {
      id: 'hiring',
      label: 'Hiring',
      count: 4
    }, {
      id: 'leaving',
      label: 'Leaving',
      count: 3
    }, {
      id: 'performance',
      label: 'Performance',
      count: 1
    }, {
      id: 'confidentiality',
      label: 'Confidentiality',
      count: 1
    }],
    workplace: [{
      id: 'all',
      label: 'All',
      count: null
    }, {
      id: 'health-safety',
      label: 'Health & Safety',
      count: 5
    }, {
      id: 'conduct',
      label: 'Conduct',
      count: 3
    }, {
      id: 'technology',
      label: 'Technology',
      count: 2
    }, {
      id: 'accommodation',
      label: 'Accommodation',
      count: 1
    }],
    business: [{
      id: 'all',
      label: 'All',
      count: null
    }, {
      id: 'services',
      label: 'Services',
      count: 6
    }, {
      id: 'partnerships',
      label: 'Partnerships',
      count: 3
    }, {
      id: 'sales',
      label: 'Sales',
      count: 4
    }, {
      id: 'licensing',
      label: 'Licensing',
      count: 2
    }],
    renting: [{
      id: 'all',
      label: 'All',
      count: null
    }, {
      id: 'residential',
      label: 'Residential',
      count: 5
    }, {
      id: 'commercial',
      label: 'Commercial',
      count: 3
    }, {
      id: 'subletting',
      label: 'Subletting',
      count: 2
    }],
    strata: [{
      id: 'all',
      label: 'All',
      count: null
    }, {
      id: 'bylaws',
      label: 'Bylaws',
      count: 4
    }, {
      id: 'governance',
      label: 'Governance',
      count: 3
    }, {
      id: 'maintenance',
      label: 'Maintenance',
      count: 2
    }],
    money: [{
      id: 'all',
      label: 'All',
      count: null
    }, {
      id: 'loans',
      label: 'Loans',
      count: 4
    }, {
      id: 'debt',
      label: 'Debt',
      count: 3
    }, {
      id: 'investment',
      label: 'Investment',
      count: 2
    }, {
      id: 'guarantor',
      label: 'Guarantor',
      count: 1
    }],
    family: [{
      id: 'all',
      label: 'All',
      count: null
    }, {
      id: 'marriage',
      label: 'Marriage',
      count: 3
    }, {
      id: 'children',
      label: 'Children',
      count: 2
    }, {
      id: 'separation',
      label: 'Separation',
      count: 4
    }, {
      id: 'personal-assets',
      label: 'Personal Assets',
      count: 2
    }],
    wills: [{
      id: 'all',
      label: 'All',
      count: null
    }, {
      id: 'wills',
      label: 'Wills',
      count: 5
    }, {
      id: 'power-of-attorney',
      label: 'Power of Attorney',
      count: 3
    }, {
      id: 'trusts',
      label: 'Trusts',
      count: 2
    }, {
      id: 'estate-planning',
      label: 'Estate Planning',
      count: 2
    }],
    'home-services': [{
      id: 'all',
      label: 'All',
      count: null
    }, {
      id: 'maintenance',
      label: 'Maintenance',
      count: 3
    }, {
      id: 'renovations',
      label: 'Renovations',
      count: 2
    }, {
      id: 'cleaning',
      label: 'Cleaning',
      count: 2
    }],
    'en-francais': [{
      id: 'all',
      label: 'Tous',
      count: null
    }, {
      id: 'emploi',
      label: 'Emploi',
      count: 2
    }, {
      id: 'affaires',
      label: 'Affaires',
      count: 2
    }, {
      id: 'famille',
      label: 'Famille',
      count: 2
    }, {
      id: 'immobilier',
      label: 'Immobilier',
      count: 1
    }]
  };
  const currentSubCategories = selectedCategory !== 'all' ? subCategories[selectedCategory] || [] : [];
  const categoryOrder = categories.map(c => c.id);
  const filteredTemplates = MOCK_TEMPLATES.filter(template => {
    if (selectedCategory !== 'all' && template.mainCategory !== selectedCategory) {
      return false;
    }
    if (selectedSubCategory !== 'all' && template.subcategory !== selectedSubCategory) {
      return false;
    }
    if (selectedLanguage !== 'all' && template.language !== selectedLanguage) {
      return false;
    }
    if (searchQuery && !template.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  }).sort((a, b) => {
    if (selectedCategory === 'all') {
      const aIndex = categoryOrder.indexOf(a.mainCategory);
      const bIndex = categoryOrder.indexOf(b.mainCategory);
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    }
    return 0;
  });
  const documentCount = filteredTemplates.length;
  const langBaseTemplates = MOCK_TEMPLATES.filter(template => {
    if (selectedCategory !== 'all' && template.mainCategory !== selectedCategory) return false;
    if (selectedSubCategory !== 'all' && template.subcategory !== selectedSubCategory) return false;
    if (searchQuery && !template.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });
  const allLangCount = langBaseTemplates.length;
  const enCount = langBaseTemplates.filter(t => t.language === 'EN').length;
  const frCount = langBaseTemplates.filter(t => t.language === 'FR').length;
  return <div className="flex flex-col h-screen bg-white" data-id="element-1230">
      <DashboardHeader categories={['Business', 'HR']} onMenuClick={handleMenuClick} onNavigateToAICopilot={handleNavigateToAICopilot} onNavigate={handleNavigate} data-id="element-1231" />

      <PrefillSelectionModal isOpen={showPrefillModal} onClose={handlePrefillCancel} onContinue={handlePrefillContinue} businesses={MOCK_USER_BUSINESSES} familyMembers={MOCK_USER_FAMILY} employees={MOCK_USER_EMPLOYEES} documentType={selectedTemplate?.title || 'Document'} data-id="element-1232" />

      <TemplateDetailsSidebar isOpen={showTemplateSidebar} onClose={handleCloseSidebar} template={selectedTemplate} onViewSample={handleViewSample} onCreateDocument={handleCreateDocumentFromSidebar} data-id="element-1233" />

      {/* Upgrade Modal */}
      {showUpgradeModal && <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowUpgradeModal(false)} data-id="element-1234" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1235">
            <div className="bg-white rounded-2xl max-w-5xl w-full p-8 relative" data-id="element-1236">
              <button onClick={() => setShowUpgradeModal(false)} className="absolute top-6 right-6 text-secondary hover:text-primary" data-id="element-1237">
                <Plus className="w-6 h-6 rotate-45" data-id="element-1238" />
              </button>

              <h2 className="text-2xl font-bold text-primary mb-2" data-id="element-1239">
                Upgrade your plan
              </h2>
              <p className="text-secondary mb-8" data-id="element-1240">
                This document requires a higher subscription tier.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1241">
                {/* Starter Plan */}
                <div className="border border-borderColor rounded-xl p-6" data-id="element-1242">
                  <div className="text-xs font-semibold text-secondary uppercase tracking-wider mb-1" data-id="element-1243">
                    INDIVIDUAL
                  </div>
                  <div className="text-2xl font-bold text-primary mb-1" data-id="element-1244">
                    Starter
                  </div>
                  <div className="mb-6" data-id="element-1245">
                    <span className="text-4xl font-bold text-primary" data-id="element-1246">$9</span>
                    <span className="text-secondary" data-id="element-1247">/mo</span>
                  </div>
                  <ul className="space-y-3 mb-6 text-sm text-primary" data-id="element-1248">
                    <li data-id="element-1249">• Up to 10 documents/mo</li>
                    <li data-id="element-1250">• Basic templates</li>
                    <li data-id="element-1251">• Email support</li>
                    <li data-id="element-1252">• EN & FR documents</li>
                  </ul>
                  <button className="w-full py-3 border border-borderColor rounded-lg text-primary font-medium hover:bg-aliceBlue transition-colors" data-id="element-1253">
                    Select plan
                  </button>
                </div>

                {/* Pro Plan - Highlighted */}
                <div className="border-2 border-violetBlue rounded-xl p-6 relative" data-id="element-1254">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-violetBlue text-white text-xs font-semibold px-4 py-1 rounded-full" data-id="element-1255">
                    Required for this doc
                  </div>
                  <div className="text-xs font-semibold text-secondary uppercase tracking-wider mb-1" data-id="element-1256">
                    INDIVIDUAL
                  </div>
                  <div className="text-2xl font-bold text-primary mb-1" data-id="element-1257">
                    Pro
                  </div>
                  <div className="mb-6" data-id="element-1258">
                    <span className="text-4xl font-bold text-primary" data-id="element-1259">$19</span>
                    <span className="text-secondary" data-id="element-1260">/mo</span>
                  </div>
                  <ul className="space-y-3 mb-6 text-sm text-primary" data-id="element-1261">
                    <li data-id="element-1262">• Unlimited documents</li>
                    <li data-id="element-1263">• All premium templates</li>
                    <li data-id="element-1264">• Priority support</li>
                    <li data-id="element-1265">• E-signature included</li>
                    <li data-id="element-1266">• Will & estate documents</li>
                  </ul>
                  <button className="w-full py-3 bg-violetBlue text-white rounded-lg font-medium hover:bg-violetBlue/90 transition-colors" data-id="element-1267">
                    Upgrade now
                  </button>
                </div>

                {/* Business Pro Plan */}
                <div className="border border-borderColor rounded-xl p-6" data-id="element-1268">
                  <div className="text-xs font-semibold text-secondary uppercase tracking-wider mb-1" data-id="element-1269">
                    BUSINESS
                  </div>
                  <div className="text-2xl font-bold text-primary mb-1" data-id="element-1270">
                    Business Pro
                  </div>
                  <div className="mb-6" data-id="element-1271">
                    <span className="text-4xl font-bold text-primary" data-id="element-1272">$99</span>
                    <span className="text-secondary" data-id="element-1273">/mo</span>
                  </div>
                  <ul className="space-y-3 mb-6 text-sm text-primary" data-id="element-1274">
                    <li data-id="element-1275">• Unlimited team members</li>
                    <li data-id="element-1276">• All individual features</li>
                    <li data-id="element-1277">• Custom contracts</li>
                    <li data-id="element-1278">• API access</li>
                    <li data-id="element-1279">• Strata & commercial docs</li>
                  </ul>
                  <button className="w-full py-3 border border-borderColor rounded-lg text-primary font-medium hover:bg-aliceBlue transition-colors" data-id="element-1280">
                    Select plan
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:pl-[100px]" data-id="element-1281">
        <div className="max-w-[1600px] mx-auto space-y-6" data-id="element-1282">
          {/* Page Title */}
          <div className="mb-6" data-id="element-1283">
            <h1 className="text-3xl font-bold text-primary mb-2" data-id="element-1284">
              My Templates
            </h1>
            <p className="text-secondary" data-id="element-1285">
              Browse your full document library — no legal knowledge needed.
            </p>
          </div>

          {/* Search Bar + Language Filters */}
          <div className="flex items-center gap-3" data-id="element-1286">
            <div className="relative flex-1 group" data-id="element-1287">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-1.5" data-id="element-1288">
                <Sparkles className="w-4 h-4 text-violetBlue" data-id="element-1289" />
              </div>
              <input type="text" placeholder="Can't find what you need? Ask me — I can help." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-11 pr-20 py-3.5 bg-white border border-borderColor rounded-xl text-sm focus:outline-none focus:border-violetBlue focus:ring-2 focus:ring-violetBlue/15 shadow-sm hover:shadow-md hover:border-violetBlue/30 transition-all" data-id="element-1290" />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2" data-id="element-1291">
                <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-aliceBlue border border-borderColor rounded text-[10px] text-disabled font-medium" data-id="element-1292">
                  ⌘K
                </kbd>
                <Search className="w-4 h-4 text-disabled" data-id="element-1293" />
              </div>
            </div>
            <div className="flex items-center rounded-full bg-[#ECEDF0] p-1 flex-shrink-0" data-id="element-1294">
              <button onClick={() => setSelectedLanguage('all')} className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-all whitespace-nowrap rounded-full ${selectedLanguage === 'all' ? 'bg-white text-primary shadow-sm' : 'text-secondary hover:text-primary'}`} data-id="element-1295">
                All
                <span className={`text-xs ${selectedLanguage === 'all' ? 'text-secondary' : 'text-disabled'}`} data-id="element-1296">
                  {allLangCount}
                </span>
              </button>
              <button onClick={() => setSelectedLanguage('EN')} className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-all whitespace-nowrap rounded-full ${selectedLanguage === 'EN' ? 'bg-white text-primary shadow-sm' : 'text-secondary hover:text-primary'}`} data-id="element-1297">
                <span className="text-base leading-none" data-id="element-1298">🇨🇦</span>
                EN
                <span className={`text-xs ${selectedLanguage === 'EN' ? 'text-secondary' : 'text-disabled'}`} data-id="element-1299">
                  {enCount}
                </span>
              </button>
              <button onClick={() => setSelectedLanguage('FR')} className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-all whitespace-nowrap rounded-full ${selectedLanguage === 'FR' ? 'bg-white text-primary shadow-sm' : 'text-secondary hover:text-primary'}`} data-id="element-1300">
                <img src="https://digilabs.nyc3.digitaloceanspaces.com/lawvo/53e79ee3fdf169e2a1f904bdb8be8c77.png" alt="Quebec" className="w-5 h-5 object-contain" data-id="element-1301" />
                FR
                <span className={`text-xs ${selectedLanguage === 'FR' ? 'text-secondary' : 'text-disabled'}`} data-id="element-1302">
                  {frCount}
                </span>
              </button>
            </div>
          </div>

          {/* Category Icons Row */}
          <div className="relative" data-id="element-1303">
            <div className="flex gap-3 overflow-x-auto pb-2" data-id="element-1304">
              {categories.map(category => {
              const Icon = category.icon;
              const isSelected = selectedCategory === category.id;
              const colors = categoryColors[category.id] || categoryColors.all;
              return <button key={category.id} onClick={() => {
                setSelectedCategory(category.id);
                setSelectedSubCategory('all');
              }} className={`flex flex-col items-center justify-center w-[120px] min-w-[120px] max-w-[120px] p-4 rounded-xl border transition-all ${isSelected ? 'shadow-sm' : 'bg-white border-borderColor hover:border-violetBlue/50'}`} style={isSelected ? {
                backgroundColor: colors.bg,
                borderColor: colors.border,
                color: colors.color
              } : undefined} data-id="element-1305">
                    <Icon className={`w-8 h-8 mb-2 ${isSelected ? '' : 'text-secondary'}`} style={isSelected ? {
                  color: colors.color
                } : undefined} data-id="element-1306" />
                    <span className={`text-xs text-center ${isSelected ? 'font-semibold' : 'text-secondary'}`} style={isSelected ? {
                  color: colors.color
                } : undefined} data-id="element-1307">
                      {category.label}
                    </span>
                  </button>;
            })}
            </div>
            {/* Right fade overlay to hint at more categories */}
            <div className="pointer-events-none absolute top-0 right-0 bottom-2 w-16 bg-gradient-to-l from-white to-transparent" data-id="element-1308" />
          </div>

          {/* Sub-category Pills */}
          {currentSubCategories.length > 0 && <div className="bg-aliceBlue/50 rounded-xl p-4" data-id="element-1309">
              <div className="flex gap-2 flex-wrap" data-id="element-1310">
                {currentSubCategories.map(subCat => {
              const isSelected = selectedSubCategory === subCat.id;
              const colors = categoryColors[selectedCategory] || categoryColors.all;
              return <button key={subCat.id} onClick={() => setSelectedSubCategory(subCat.id)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${isSelected ? 'text-white' : 'bg-white text-primary border border-borderColor hover:border-violetBlue'}`} style={isSelected ? {
                backgroundColor: colors.color
              } : undefined} data-id="element-1311">
                      {subCat.label}
                      {subCat.count !== null && <span className="ml-2" data-id="element-1312">{subCat.count}</span>}
                    </button>;
            })}
              </div>
            </div>}

          {/* Document Count */}
          <div className="text-sm text-secondary" data-id="element-1313">
            {documentCount} documents
            {selectedCategory !== 'all' && ` · ${categories.find(c => c.id === selectedCategory)?.label}`}
          </div>

          {/* Template Cards */}
          {selectedCategory === 'all' ? <div className="space-y-8" data-id="element-1314">
              {categories.filter(c => c.id !== 'all').map(category => {
            const groupTemplates = filteredTemplates.filter(t => t.mainCategory === category.id);
            if (groupTemplates.length === 0) return null;
            const colors = categoryColors[category.id] || categoryColors.all;
            const CategoryIcon = category.icon;
            return <div key={category.id} data-id="element-1315">
                      {/* Category Group Header */}
                      <div className="flex items-center gap-3 mb-4" data-id="element-1316">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
                  backgroundColor: colors.bg
                }} data-id="element-1317">
                          <CategoryIcon className="w-4 h-4" style={{
                    color: colors.color
                  }} data-id="element-1318" />
                        </div>
                        <h2 className="text-lg font-bold text-primary" data-id="element-1319">
                          {category.label}
                        </h2>
                        <span className="text-sm text-secondary" data-id="element-1320">
                          {groupTemplates.length} documents
                        </span>
                      </div>
                      {/* Cards Row - horizontal scroll with fade hint */}
                      <div className="relative" data-id="element-1321">
                        <div className="flex gap-4 overflow-x-auto pb-3 -mx-1 px-1 scrollbar-hide" data-id="element-1322">
                          {groupTemplates.map(template => <div key={template.id} className="w-[250px] min-w-[250px] flex-shrink-0" data-id="element-1323">
                              <TemplateCard template={template} onTemplateClick={handleTemplateClick} onUpgradeClick={() => setShowUpgradeModal(true)} isFeatured={FEATURED_IDS.has(template.id)} data-id="element-1324" />
                            </div>)}
                        </div>
                        {/* Right fade overlay to hint at more content */}
                        <div className="pointer-events-none absolute top-0 right-0 bottom-3 w-16 bg-gradient-to-l from-white to-transparent" data-id="element-1325" />
                      </div>
                    </div>;
          })}
            </div> : <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4" data-id="element-1326">
              {filteredTemplates.map(template => <div key={template.id} className="w-full sm:w-[250px] sm:flex-shrink-0" data-id="element-1327">
                  <TemplateCard template={template} onTemplateClick={handleTemplateClick} onUpgradeClick={() => setShowUpgradeModal(true)} isFeatured={FEATURED_IDS.has(template.id)} data-id="element-1328" />
                </div>)}
            </div>}
        </div>
      </main>
    </div>;
}